import { useState } from 'react'
import { Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { FA_COLOR, FA_ORDER, fmtTime } from '@/components/researcher/liveDemo/constants'
import type { PrototypeResults, PrototypeScreen } from '@/services/prototypeService'
import GazeHeatmap from './GazeHeatmap'

type Layer = 'clicks' | 'gaze'

function pct(v: number | null | undefined) {
  return v == null ? '—' : `${Math.round(v * 100)}%`
}

/**
 * Results of a Figma prototype task, screen by screen (FaceWise Pro, Z-98).
 *
 * A prototype has no shared time axis, so this — not the averaged curve — is
 * the aggregate view: the task outcome on top, then every screen people
 * reached with where they clicked (red = no hotspot there), what their faces
 * did while on it and, with eye tracking, where they looked.
 */
export default function PrototypeResultsPanel({ results }: { results: PrototypeResults }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')
  const { t: tEmo } = useTranslation('liveDemo')
  const [layer, setLayer] = useState<Layer>('clicks')
  const summary = results.summary

  const tile = (label: string, value: string, hint?: string) => (
    <Box sx={{ flex: '1 1 140px', p: 1.5, borderRadius: 2, bgcolor: c.gray50 }}>
      <Typography sx={{ fontSize: 11.5, color: c.ink56, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {hint && <Typography sx={{ fontSize: 11.5, color: c.ink48 }}>{hint}</Typography>}
    </Box>
  )

  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{t('prototype.title')}</Typography>
          <Typography sx={{ fontSize: 13, color: c.ink56 }}>{t('prototype.task', { task: results.task })}</Typography>
        </Box>

        {summary && summary.sessions > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
            {summary.has_goal && tile(t('prototype.successRate'), pct(summary.success_rate), t('prototype.ofSessions', { success: summary.success, count: summary.sessions }))}
            {summary.has_goal &&
              tile(t('prototype.medianSuccess'), summary.median_success_ms != null ? fmtTime(summary.median_success_ms / 1000) : '—')}
            {!summary.has_goal &&
              tile(t('prototype.medianDuration'), summary.median_duration_ms != null ? fmtTime(summary.median_duration_ms / 1000) : '—')}
            {tile(t('prototype.gaveUp'), String(summary.gave_up), t('prototype.timeouts', { count: summary.timeout }))}
            {tile(t('prototype.misclickRate'), pct(summary.misclick_rate), t('prototype.misclickHint'))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: c.ink56 }}>{t('prototype.noSessions')}</Typography>
        )}

        {results.screens.length > 0 && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{t('prototype.screens')}</Typography>
            {results.eye_tracking && (
              <ToggleButtonGroup size="small" exclusive value={layer} onChange={(_, v: Layer | null) => v && setLayer(v)}>
                <ToggleButton value="clicks">{t('prototype.layerClicks')}</ToggleButton>
                <ToggleButton value="gaze">{t('prototype.layerGaze')}</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Stack>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {results.screens.map((s) => (
            <ScreenCard key={s.node_id} screen={s} layer={layer} emotionLabel={(k) => tEmo(`emotions.${k}`)} />
          ))}
        </Box>
        {layer === 'gaze' && (
          <Typography sx={{ fontSize: 11.5, color: c.ink48 }}>{t('prototype.gazeHint')}</Typography>
        )}
      </Stack>
    </Paper>
  )
}

function ScreenCard({ screen, layer, emotionLabel }: { screen: PrototypeScreen; layer: Layer; emotionLabel: (k: string) => string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')
  const points = layer === 'gaze' ? [] : screen.clicks
  const topEmotions = screen.emotions
    ? FA_ORDER.map((k) => ({ k, v: screen.emotions![k] ?? 0 })).sort((a, b) => b.v - a.v)
    : []

  return (
    <Box sx={{ border: `1px solid ${c.ink08}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
      <Box sx={{ position: 'relative', bgcolor: '#0B0C1E', height: 260, display: 'grid', placeItems: 'center' }}>
        <Box sx={{ position: 'relative', height: '100%', maxWidth: '100%', aspectRatio: `${screen.width || 16} / ${screen.height || 9}` }}>
          {screen.image_url ? (
            <Box component="img" src={screen.image_url} alt={screen.name} sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          ) : (
            <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: 'rgba(255,255,255,.6)', fontSize: 12 }}>
              {screen.name}
            </Box>
          )}
          {layer === 'gaze' && <GazeHeatmap points={screen.gaze ?? []} />}
          {points.map((p, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                left: `${p.x * 100}%`,
                top: `${p.y * 100}%`,
                width: 12,
                height: 12,
                ml: '-6px',
                mt: '-6px',
                borderRadius: '50%',
                bgcolor: p.handled ? 'rgba(95,179,168,.7)' : 'rgba(234,80,72,.75)',
                border: '1.5px solid #fff',
                pointerEvents: 'none',
              }}
            />
          ))}
        </Box>
      </Box>
      <Box sx={{ p: 1.25 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {screen.name}
          </Typography>
          {screen.is_start && <Tag label={t('prototype.start')} />}
          {screen.is_goal && <Tag label={t('prototype.goal')} accent />}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: c.ink56 }}>
          {t('prototype.screenStats', {
            respondents: screen.respondents,
            time: fmtTime(screen.median_time_ms / 1000),
            misclicks: screen.misclicks,
          })}
        </Typography>
        {topEmotions.length > 0 && (
          <Tooltip title={topEmotions.map(({ k, v }) => `${emotionLabel(k)}: ${Math.round(v * 100)}%`).join(' · ')}>
            <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', mt: 1 }}>
              {topEmotions.map(({ k, v }) => (
                <Box key={k} sx={{ width: `${v * 100}%`, bgcolor: FA_COLOR[k] }} />
              ))}
            </Box>
          </Tooltip>
        )}
        {topEmotions.length > 0 && (
          <Typography sx={{ fontSize: 11.5, color: c.ink56, mt: 0.5 }}>
            {t('prototype.dominant', {
              emotion: emotionLabel(topEmotions.find((e) => e.k !== 'neutral')?.k ?? 'neutral'),
            })}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

function Tag({ label, accent = false }: { label: string; accent?: boolean }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box
      component="span"
      sx={{ px: 0.75, py: 0.1, borderRadius: 10, fontSize: 10.5, fontWeight: 700, bgcolor: accent ? c.coralSoft : c.gray100, color: accent ? c.coral : c.ink72 }}
    >
      {label}
    </Box>
  )
}
